import { ApolloProvider } from '@apollo/client';
import React from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { createRoot } from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import { Provider } from 'react-redux';
import { createBrowserRouter, createRoutesFromElements } from 'react-router';
import { RouterProvider } from 'react-router/dom';
import { persistStore } from 'redux-persist';
import { PersistGate } from 'redux-persist/es/integration/react';
import ErrorBoundary from '../components/ErrorBoundary';
import DefaultNamespaceFormatsProvider from '../editor/DefaultNamespaceFormatsProvider';
import { createApolloClient } from './apollo/getApolloClient';
import appRoutes from './appRoutes';
import getErrorLink from './getErrorLink';
import getReduxStore from './redux/getReduxStore';
import RootErrorBoundaryFallback from './RootErrorBoundaryFallback';

const app = async () => {
  const store = getReduxStore();
  const persistor = persistStore(store);

  const apolloClient = await createApolloClient({
    errorLink: getErrorLink({
      getClient: () => apolloClient,
      store,
    }),
  });

  const sentryCreateBrowserRouter =
    window.SentryTracing?.wrapCreateBrowserRouter(createBrowserRouter) ??
    createBrowserRouter;

  const router = sentryCreateBrowserRouter(createRoutesFromElements(appRoutes));

  const root = createRoot(document.getElementById('root') as HTMLElement);
  root.render(
    <ErrorBoundary
      name="root"
      renderFallback={({ type }) => (
        <RootErrorBoundaryFallback
          type={type}
          store={store}
          client={apolloClient}
        />
      )}
    >
      <DefaultNamespaceFormatsProvider>
        <Provider store={store}>
          <ApolloProvider client={apolloClient}>
            <PersistGate loading={null} persistor={persistor}>
              <HelmetProvider>
                <DndProvider backend={HTML5Backend}>
                  <RouterProvider router={router} />
                </DndProvider>
              </HelmetProvider>
            </PersistGate>
          </ApolloProvider>
        </Provider>
      </DefaultNamespaceFormatsProvider>
    </ErrorBoundary>,
  );
};

export default app;
